import React, { useState, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation } from 'react-router-dom';
// Eager: order-entry critical path, layout, route guards, and modals shown on mount.
import OrderForm from './components/OrderForm';
import StaffMemberModal from './components/StaffMemberModal';
import AuthGuard from './components/AuthGuard';
import TermsGate from './components/TermsGate';
import { BillingOnlyGate } from './components/BillingOnlyScreen';
import { DriverPasswordChangeGate } from './components/DriverPasswordChangeScreen';
import Sidebar from './components/Sidebar';
import GettingStarted from './components/GettingStarted';
import DriverRoute from './components/routes/DriverRoute';
import StaffRoute from './components/routes/StaffRoute';
import { NotificationContainer } from './components/Notification';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import IssueToasts from './components/IssueToasts';
import { useVersionCheck } from './hooks/useVersionCheck';
import MobileBottomNav from './components/MobileBottomNav';
import { KeyboardShortcutsProvider } from './contexts/KeyboardShortcuts';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { AppearanceProvider } from './contexts/AppearanceContext';
import { ShopIdentityProvider } from './contexts/ShopIdentityContext';
import { OrderLayoutProvider } from './contexts/OrderLayoutContext';
import { isTrackingCodePath, isReviewCodePath } from './utils/publicLinkPaths';
import { isFullScreenOverlay } from './utils/fullScreenPaths';
import { lazyWithReload } from './utils/lazyWithReload';
import LoadErrorBoundary from './components/LoadErrorBoundary';
import PlanFeatureGate from './components/PlanFeatureGate';
import MoveToCoreBanner from './components/MoveToCoreBanner';
import TrialBanner from './components/TrialBanner';
import PastDueBanner from './components/PastDueBanner';
import './utils/googleMapsLoader'; // Load Google Maps globally on app start

// Lazy: route-level screens are code-split into their own chunks, loaded on first
// navigation. Keeps the initial bundle (POS/order-entry path) small — the map-heavy
// DeliveryDispatch, Reports, Events, Proposals, etc. no longer ship in main.js.
const Dashboard = lazyWithReload(() => import('./components/Dashboard'));
const Orders = lazyWithReload(() => import('./components/Orders'));
const OrderDetails = lazyWithReload(() => import('./components/OrderDetails'));
const Customers = lazyWithReload(() => import('./components/Customers'));
const CustomerDetail = lazyWithReload(() => import('./components/CustomerDetail'));
const DeliveryDispatch = lazyWithReload(() => import('./components/DeliveryDispatch'));
const Reports = lazyWithReload(() => import('./components/Reports'));
const IssuesList = lazyWithReload(() => import('./components/IssuesList'));
const Waste = lazyWithReload(() => import('./components/Waste'));
const WeeklyScan = lazyWithReload(() => import('./components/WeeklyScan'));
const Register = lazyWithReload(() => import('./components/register/Register'));
const Formulas = lazyWithReload(() => import('./components/Formulas'));
const Events = lazyWithReload(() => import('./components/events/Events'));
const Production = lazyWithReload(() => import('./components/production/Production'));
const InstallBoard = lazyWithReload(() => import('./components/production/InstallBoard'));
const EventDetails = lazyWithReload(() => import('./components/events/EventDetails'));
const ProposalsList = lazyWithReload(() => import('./components/proposals/ProposalsList'));
const ProposalEditor = lazyWithReload(() => import('./components/proposals/ProposalEditor'));
const PublicProposalPage = lazyWithReload(() => import('./components/PublicProposalPage'));
const BrideVisionForm = lazyWithReload(() => import('./components/BrideVisionForm'));
const BookingPacket = lazyWithReload(() => import('./components/BookingPacket'));
const WasteLogForm = lazyWithReload(() => import('./components/WasteLogForm'));
const DeliveryTracking = lazyWithReload(() => import('./components/DeliveryTracking'));
const ReviewRedirect = lazyWithReload(() => import('./components/tracking/ReviewRedirect'));
const ShopSignup = lazyWithReload(() => import('./components/ShopSignup'));
const UserManagement = lazyWithReload(() => import('./components/UserManagement'));
const Profile = lazyWithReload(() => import('./components/Profile'));
const DriverDashboard = lazyWithReload(() => import('./components/driver/DriverDashboard'));
const ScanStation = lazyWithReload(() => import('./components/ScanStation'));
const PendingRecurringReview = lazyWithReload(() => import('./components/PendingRecurringReview'));
const SettingsDrawer = lazyWithReload(() => import('./components/SettingsDrawer'));
const POSInstructionsDemo = lazyWithReload(() => import('./components/POSInstructionsDemo'));
const IndustryNews = lazyWithReload(() => import('./components/IndustryNews'));
const StoreProductsPage = lazyWithReload(() => import('./components/store/ProductsPage'));
const StoreCollectionsPage = lazyWithReload(() => import('./components/store/CollectionsPage'));
const StoreAddonsPage = lazyWithReload(() => import('./components/store/AddonsPage'));
const StoreThemePage = lazyWithReload(() => import('./components/store/ThemePage'));
const StorePagesPage = lazyWithReload(() => import('./components/store/PagesPage'));
const StorePageEditor = lazyWithReload(() => import('./components/store/PageEditor'));
const StoreMenusPage = lazyWithReload(() => import('./components/store/MenusPage'));
const StoreDiscountsPage = lazyWithReload(() => import('./components/store/DiscountsPage'));
const StoreGiftCardsPage = lazyWithReload(() => import('./components/store/GiftCardsPage'));
const StoreCustomersPage = lazyWithReload(() => import('./components/store/CustomersPage'));
const StoreImportPage = lazyWithReload(() => import('./components/store/ImportPage'));
const StoreRedirectsPage = lazyWithReload(() => import('./components/store/RedirectsPage'));
const StoreInboundOrdersPage = lazyWithReload(() => import('./components/store/InboundOrdersPage'));
const StoreOverviewPage = lazyWithReload(() => import('./components/store/OverviewPage'));
const StoreDomainPage = lazyWithReload(() => import('./components/store/DomainPage'));

// Fallback shown while a lazy route chunk is fetched.
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-floral-600"></div>
    </div>
  );
}

// Wrapper component for standalone order details page
function OrderDetailsPage({ addNotification }) {
  const { orderNumber } = useParams();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'details';

  // Full bleed: the order's PageHeader band runs edge to edge, so this undoes
  // the shared route padding (px-2 sm:px-4 lg:px-6 py-6) around it.
  return (
    <div className="-mx-2 -my-6 sm:-mx-4 lg:-mx-6">
      <OrderDetails
        orderNumber={orderNumber}
        onNotification={addNotification}
        standalone={true}
        initialTab={initialTab}
      />
    </div>
  );
}


// Wrapper component for standalone customer detail page
function CustomerDetailPage({ onNotification }) {
  const { customerId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get('tab') || null;

  // Full bleed: the customer's PageHeader band runs edge to edge, so this
  // undoes the shared route padding (px-2 sm:px-4 lg:px-6 py-6) around it.
  return (
    <div className="-mx-2 -my-6 sm:-mx-4 lg:-mx-6">
      <CustomerDetail
        customerId={customerId}
        onNotification={onNotification}
        initialTab={initialTab}
      />
    </div>
  );
}

// Main app content component - Force rebuild for env vars
function AppContent() {
  const { canDrive, user, initialized } = useAuth();
  const location = useLocation();
  // Order entry runs without the app shell — see the Sidebar note below.
  const isOrderEntry = location.pathname === '/create-order';
  // The theme editor is a full-screen overlay; it drops the shell too.
  const hidesShell = isOrderEntry || isFullScreenOverlay(location.pathname);
  const [notifications, setNotifications] = useState([]);
  const [showStaffMemberModal, setShowStaffMemberModal] = useState(false);
  const versionCheck = useVersionCheck();

  // Notification management
  // Errors get a longer default (10s) so staff have time to read and act on them.
  // Success/info/warning keep the standard 5s.
  const addNotification = useCallback((message, type = 'info', duration) => {
    const resolvedDuration = duration ?? (type === 'error' ? 10000 : 5000);
    const id = Date.now() + Math.random();
    const notification = { id, message, type, duration: resolvedDuration };
    setNotifications(prev => [...prev, notification]);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Handle order completion
  const handleOrderComplete = useCallback((order) => {
    addNotification(`Order #${order.orderNumber} created successfully!`, 'success');
  }, [addNotification]);

  // Check if staff account needs to select a staff member
  React.useEffect(() => {
    if (initialized && user && user.accountType === 'staff') {
      // Check if staff member is already selected
      const savedStaffMember = localStorage.getItem('selectedStaffMember');
      if (!savedStaffMember && user.staffMembers && user.staffMembers.length > 0) {
        // No staff member selected - show modal
        setShowStaffMemberModal(true);
      }
    }
  }, [initialized, user]);

  // Handle staff member selection
  const handleStaffMemberSelected = useCallback((staff) => {
    setShowStaffMemberModal(false);
    addNotification(`Creating orders as ${staff.firstName} ${staff.lastName}`, 'success', 3000);
  }, [addNotification]);

  // Full UI with sidebar and all routes
  return (
    <div className="h-screen bg-surface-alt flex overflow-hidden">
      {/* Staff Member Selection Modal */}
      <StaffMemberModal
        isOpen={showStaffMemberModal}
        onClose={() => setShowStaffMemberModal(false)}
        onSelect={handleStaffMemberSelected}
        title="Select Staff Member"
        subtitle="Please select which staff member is creating orders"
      />

      {/* Sidebar Navigation. Hidden on order entry: the design treats taking an
          order as a focused task with a "Dashboard ←" way out, not another
          screen inside the shell. Applies to both order-entry layouts, so a
          timing comparison between them isolates the column change. */}
      {!hidesShell && <Sidebar />}

      {/* Main Content Area */}
      <MainContentArea
        notifications={notifications}
        onNotification={addNotification}
        removeNotification={removeNotification}
        handleOrderComplete={handleOrderComplete}
      />

      <UpdateAvailableToast
        status={versionCheck.status}
        deployment={versionCheck.deployment}
        loadedCommit={versionCheck.loadedCommit}
        justDeployed={versionCheck.justDeployed}
        onRefresh={versionCheck.refresh}
        onDismiss={versionCheck.dismiss}
      />

      <IssueToasts />
    </div>
  );
}

// Separate component for main content to use useLocation
function MainContentArea({ notifications, onNotification, removeNotification, handleOrderComplete }) {
  const location = useLocation();
  const isDriverRoute = location.pathname === '/driver';
  const isDispatch = location.pathname === '/dispatch';
  const fullScreen = isFullScreenOverlay(location.pathname);

  console.log('[MainContentArea] pathname:', location.pathname, 'isDriverRoute:', isDriverRoute);

  return (
    <div className="flex-1 flex flex-col w-full max-w-full overflow-x-hidden overflow-y-auto">
      {/* Studio S4: "move to Core" prompt after two months over the order cap. Renders nothing for everyone else. */}
      {/* QA B P1-5: an unpaid plan bill, with the days left before the shop closes, on every page for owners and admins. */}
      {!isDriverRoute && !isDispatch && <PastDueBanner />}
      {!isDriverRoute && !isDispatch && <TrialBanner />}
      {!isDriverRoute && !isDispatch && <MoveToCoreBanner />}
      {/* Main Content — single Suspense boundary covers all lazy route chunks */}
      <LoadErrorBoundary resetKey={location.pathname} message="Bouqmark couldn't load this page.">
      <Suspense fallback={<RouteFallback />}>
      <Routes>
          {/* Dispatch route - full screen without padding */}
          <Route
            path="/dispatch"
            element={
              <StaffRoute>
                <PlanFeatureGate feature="dispatch">
                  <DeliveryDispatch onNotification={onNotification} />
                </PlanFeatureGate>
              </StaffRoute>
            }
          />

          {/* Scan Station - full screen for phone scanning */}
          <Route
            path="/scan"
            element={
              <StaffRoute>
                <PlanFeatureGate feature="dispatch">
                  <ScanStation />
                </PlanFeatureGate>
              </StaffRoute>
            }
          />

          {/* Driver dashboard - full screen without padding (mobile-optimized) */}
          <Route
            path="/driver"
            element={
              <DriverRoute>
                <PlanFeatureGate feature="dispatch">
                  <DriverDashboard />
                </PlanFeatureGate>
              </DriverRoute>
            }
          />

          {/* Settings - full-screen overlay drawer (no page padding) */}
          <Route
            path="/settings"
            element={
              <StaffRoute>
                <SettingsDrawer onNotification={onNotification} />
              </StaffRoute>
            }
          />

          {/* Online store theme editor - full-screen overlay like Settings */}
          <Route
            path="/store/theme"
            element={
              <StaffRoute>
                <StoreThemePage onNotification={onNotification} />
              </StaffRoute>
            }
          />

          {/* All other routes with standard padding */}
          <Route
            path="/*"
            element={
              <div className="px-2 sm:px-4 lg:px-6 py-6">
                <Routes>
                  <Route
                    path="/"
                    element={
                      <StaffRoute>
                        <Dashboard onNotification={onNotification} />
                      </StaffRoute>
                    }
                  />
                  <Route
                    path="/getting-started"
                    element={
                      <StaffRoute>
                        <GettingStarted onNotification={onNotification} />
                      </StaffRoute>
                    }
                  />
                  <Route
                    path="/create-order"
                    element={
                      <StaffRoute>
                        <div className="w-full">
                          <OrderForm
                            onOrderComplete={handleOrderComplete}
                            onNotification={onNotification}
                          />
                        </div>
                      </StaffRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <StaffRoute>
                        {/* No extra padding: Orders' header band runs edge to edge. */}
                        <Orders onNotification={onNotification} />
                      </StaffRoute>
                    }
                  />
            <Route
              path="/events"
              element={
                <StaffRoute>
                  <Events onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/events/:eventId"
              element={
                <StaffRoute>
                  <EventDetails onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/production"
              element={
                <StaffRoute>
                  <Production onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/install-board"
              element={
                <StaffRoute>
                  <InstallBoard onNotification={onNotification} />
                </StaffRoute>
              }
            />
                  <Route
                    path="/proposals"
                    element={
                      <StaffRoute>
                        <div className="p-2 sm:p-6">
                          <ProposalsList onNotification={onNotification} />
                        </div>
                      </StaffRoute>
                    }
                  />
                  <Route
                    path="/proposals/:id"
                    element={
                      <StaffRoute>
                        <div className="p-2 sm:p-6">
                          <ProposalEditor onNotification={onNotification} />
                        </div>
                      </StaffRoute>
                    }
                  />
            <Route
              path="/customers"
              element={
                <StaffRoute>
                  <Customers onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/house-accounts"
              element={
                <StaffRoute>
                  <PlanFeatureGate feature="house_accounts">
                    <Customers onNotification={onNotification} />
                  </PlanFeatureGate>
                </StaffRoute>
              }
            />
            <Route
              path="/customers/:customerId"
              element={
                <StaffRoute>
                  <CustomerDetailPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/recurring-review"
              element={
                <StaffRoute>
                  <PlanFeatureGate feature="recurring_orders">
                    <PendingRecurringReview onNotification={onNotification} />
                  </PlanFeatureGate>
                </StaffRoute>
              }
            />
            <Route
              path="/pos-demo"
              element={
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-2xl font-semibold text-gray-900">POS Instructions Demo</h2>
                    <p className="text-gray-500 mt-1">Test the POS instructions modal</p>
                  </div>
                  <div className="p-6">
                    <POSInstructionsDemo />
                  </div>
                </div>
              }
            />
            <Route
              path="/issues"
              element={
                <StaffRoute>
                  <IssuesList onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <StaffRoute>
                  <Reports onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/waste"
              element={
                <StaffRoute>
                  <Waste />
                </StaffRoute>
              }
            />
            <Route
              path="/register"
              element={
                <StaffRoute>
                  <Register onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/weekly-scan"
              element={
                <StaffRoute>
                  <WeeklyScan />
                </StaffRoute>
              }
            />
            <Route
              path="/formulas"
              element={
                <StaffRoute>
                  <Formulas />
                </StaffRoute>
              }
            />
            <Route
              path="/industry-news"
              element={
                <StaffRoute>
                  <IndustryNews />
                </StaffRoute>
              }
            />
            {/* Online store (Phase 1): products, collections, add-ons; Overview (8D) */}
            <Route
              path="/store"
              element={
                <StaffRoute>
                  <StoreOverviewPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/products"
              element={
                <StaffRoute>
                  <StoreProductsPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/products/import"
              element={
                <StaffRoute>
                  <StoreImportPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/domain"
              element={
                <StaffRoute>
                  <StoreDomainPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/redirects"
              element={
                <StaffRoute>
                  <StoreRedirectsPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/inbound"
              element={
                <StaffRoute>
                  <StoreInboundOrdersPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/collections"
              element={
                <StaffRoute>
                  <StoreCollectionsPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/addons"
              element={
                <StaffRoute>
                  <StoreAddonsPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/pages"
              element={
                <StaffRoute>
                  <StorePagesPage />
                </StaffRoute>
              }
            />
            <Route
              path="/store/pages/:id"
              element={
                <StaffRoute>
                  <StorePageEditor onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/menus"
              element={
                <StaffRoute>
                  <StoreMenusPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/discounts"
              element={
                <StaffRoute>
                  <StoreDiscountsPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/gift-cards"
              element={
                <StaffRoute>
                  <StoreGiftCardsPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/store/customers"
              element={
                <StaffRoute>
                  <StoreCustomersPage onNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/orders/:orderNumber"
              element={
                <StaffRoute>
                  <OrderDetailsPage addNotification={onNotification} />
                </StaffRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <StaffRoute>
                  <UserManagement />
                </StaffRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <Profile onNotification={onNotification} />
              }
            />
                </Routes>
              </div>
            }
          />
        </Routes>
      </Suspense>
      </LoadErrorBoundary>

        {/* Bottom padding for mobile nav */}
        {!isDriverRoute && !fullScreen && <div className="md:hidden h-16" />}

        {/* Notifications */}
        <NotificationContainer
          notifications={notifications}
          onRemove={removeNotification}
          position={isDispatch ? 'dispatch' : 'default'}
        />

        {/* Mobile Bottom Navigation */}
        {!isDriverRoute && !fullScreen && <MobileBottomNav />}
      </div>
    );
  }

// Main App component with keyboard shortcuts provider
function AppRoot() {
  const location = useLocation();
  if (isTrackingCodePath(location.pathname) || isReviewCodePath(location.pathname)) {
    return (
      <LoadErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/r/:code" element={<ReviewRedirect />} />
          <Route path="/:token" element={<DeliveryTracking />} />
        </Routes>
      </Suspense>
      </LoadErrorBoundary>
    );
  }
  // Self-serve trial sign-up (10B): public, before any account exists.
  if (location.pathname === '/signup' || location.pathname.startsWith('/signup/')) {
    return (
      <LoadErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RouteFallback />}>
        <ShopSignup />
      </Suspense>
      </LoadErrorBoundary>
    );
  }
  // Public, no-login proposal link lives OUTSIDE AuthProvider/AuthGuard.
  if (location.pathname.startsWith('/p/') || location.pathname.startsWith('/vision/') || location.pathname.startsWith('/booking/') || location.pathname.startsWith('/w/') || location.pathname.startsWith('/track/')) {
    return (
      <LoadErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/p/:token" element={<PublicProposalPage />} />
          <Route path="/vision/:token" element={<BrideVisionForm />} />
          <Route path="/booking/:token" element={<BookingPacket />} />
          <Route path="/w/:token" element={<WasteLogForm />} />
          <Route path="/track/:token" element={<DeliveryTracking />} />
        </Routes>
      </Suspense>
      </LoadErrorBoundary>
    );
  }
  return (
    <AuthProvider>
      {/* Inside AuthProvider so the appearance can be resolved per staff member
          (shared terminals), but outside AuthGuard so the login screen is themed too. */}
      <AppearanceProvider>
        <OrderLayoutProvider>
        <AuthGuard>
          {/* A driver on a one-time password is refused everywhere until they
              pick their own in the Driver app; they see only that instruction. */}
          <DriverPasswordChangeGate>
          {/* The clickwrap: a shop's owner accepts the Bouqmark terms before
              anyone in the shop gets into the app. */}
          <TermsGate>
          {/* A suspended shop's owner (billing-only session, billing B3) sees
              only Plan & billing; nothing below is mounted for them. */}
          <BillingOnlyGate>
          {/* Loads the shop's entitlements + onboarding state once per login;
              the sidebar and gated screens read from it. */}
          <EntitlementsProvider>
          <ShopIdentityProvider>
          <KeyboardShortcutsProvider>
            <AppContent />
          </KeyboardShortcutsProvider>
          </ShopIdentityProvider>
          </EntitlementsProvider>
          </BillingOnlyGate>
          </TermsGate>
          </DriverPasswordChangeGate>
        </AuthGuard>
        </OrderLayoutProvider>
      </AppearanceProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
      <AppRoot />
    </Router>
  );
}

export default App;
